import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import type { User as OidcUser } from "oidc-client-ts";
import type { Profile, UserRole } from "bancojs";
import type { RuntimeConfig } from "@/config";

/**
 * Autenticación OIDC y autorización por rol.
 *
 * El `UserManager` vive en un `let` de módulo y NO en el estado de Pinia: es un
 * objeto con estado interno y listeners, y envolverlo en un proxy reactivo
 * profundo lo rompe.
 */
let manager: UserManager | null = null;

function requireManager(): UserManager {
  if (!manager) throw new Error("El store de autenticación se usó antes de init()");
  return manager;
}

export const useAuthStore = defineStore("auth", () => {
  const accessToken = ref<string | null>(null);
  /** El perfil que devuelve GET /my/profile, no los claims del token. */
  const profile = ref<Profile | null>(null);
  /** true una vez que se intentó restaurar la sesión guardada. */
  const ready = ref(false);
  /** true si falló la renovación silenciosa: el guard fuerza reautenticación. */
  const renewFailed = ref(false);

  const isAuthenticated = computed(() => accessToken.value !== null);
  const roles = computed<UserRole[]>(() => profile.value?.roles ?? []);
  const userType = computed(() => profile.value?.type ?? null);

  /** ¿Tiene alguno de estos roles? */
  function hasAnyRole(required: readonly UserRole[]): boolean {
    return required.some((r) => roles.value.includes(r));
  }

  /**
   * Fuente ÚNICA de verdad de autorización, usada tanto por los guards del
   * router como por los `v-if` de los drawers. Tenerla en un solo lugar es lo
   * que impide que el menú y los guards discrepen — el defecto del
   * AgAdminDrawer anterior, que mostraba cuatro ítems fijos sin mirar los roles
   * y dejaba al usuario chocar con 403 silenciosos.
   *
   * Sin `required` significa "basta con estar autenticado".
   */
  function canAccess(required?: readonly UserRole[]): boolean {
    if (!isAuthenticated.value) return false;
    if (!required || required.length === 0) return true;
    return hasAnyRole(required);
  }

  /** ¿Puede entrar a alguna sección de administración o de sistema? */
  const hasAnyAdmin = computed(() => roles.value.some((r) => r.includes("ADMIN")));

  async function init(oidc: RuntimeConfig["oidc"]) {
    const origin = window.location.origin;
    manager = new UserManager({
      authority: oidc.authority,
      client_id: oidc.client_id,
      scope: oidc.scopes,
      redirect_uri: `${origin}/auth-callback.html`,
      silent_redirect_uri: `${origin}/silent-renew.html`,
      post_logout_redirect_uri: `${origin}/?notify=logout`,
      response_type: "code",
      // Se mantiene `fragment` porque es lo que el cliente del realm tiene
      // registrado; cambiarlo exige tocar Keycloak.
      response_mode: "fragment",
      automaticSilentRenew: true,
      // Acota el SSO silencioso. Welcome deja un skeleton hasta que
      // `signinSilent()` resuelve, así que un iframe que no responde —IDP
      // lento, red caída, bloqueo de terceros— dejaba la pantalla de login
      // cargando indefinidamente en vez de ofrecer el botón.
      silentRequestTimeoutInSeconds: 10,
      loadUserInfo: false,
      filterProtocolClaims: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      // En oidc-client-ts v3 PKCE está activo por defecto para response_type
      // "code", y `validateSubOnSilentRenew` desapareció: la validación de `sub`
      // es incondicional.
    });

    manager.events.addUserLoaded((user: OidcUser) => {
      accessToken.value = user.access_token;
      renewFailed.value = false;
    });
    manager.events.addUserUnloaded(() => {
      accessToken.value = null;
      profile.value = null;
    });
    manager.events.addUserSignedOut(() => {
      accessToken.value = null;
      profile.value = null;
    });
    // Sin cablear en la versión anterior: una renovación fallida dejaba la app
    // con un token expirado y sin forma de enterarse.
    manager.events.addSilentRenewError((err) => {
      console.error("[auth] falló la renovación silenciosa", err);
      renewFailed.value = true;
    });
    // El código anterior tenía acá un setTimeout(..., 30000) con el comentario
    // "Wait 2 minutes". Es innecesario: addUserLoaded ya dispara con el token
    // nuevo cuando la renovación termina.

    const user = await manager.getUser();
    if (user && !user.expired) {
      accessToken.value = user.access_token;
    }
    ready.value = true;
  }

  /** La identidad OIDC guardada, o null. */
  async function getIdentity(): Promise<OidcUser | null> {
    const user = await requireManager().getUser();
    return user && !user.expired ? user : null;
  }

  /**
   * Devuelve un token válido, renovándolo si hace falta. La usa el interceptor
   * de peticiones del SDK, que puede esperarla porque los interceptores de
   * bancojs v2 son async.
   */
  async function ensureFreshToken(): Promise<string | null> {
    const mgr = requireManager();
    const user = await mgr.getUser();
    if (user && !user.expired) {
      accessToken.value = user.access_token;
      return user.access_token;
    }
    try {
      const renewed = await mgr.signinSilent();
      accessToken.value = renewed?.access_token ?? null;
      return accessToken.value;
    } catch {
      renewFailed.value = true;
      accessToken.value = null;
      return null;
    }
  }

  /**
   * Fija el perfil ya obtenido de GET /my/profile. De ahí salen los roles: el
   * token no los trae, porque RolesClaimsAdapter los deriva de banco_users.
   *
   * El store NO llama a la API a propósito. El cliente de la API depende de este
   * store para su interceptor de autenticación, así que hacerlo al revés crearía
   * un ciclo de imports. Quien necesita el perfil lo pide y lo deja acá; lo
   * hace el guard beforeEach del router.
   */
  function setProfile(value: Profile | null) {
    profile.value = value;
  }

  function login(args?: Parameters<UserManager["signinRedirect"]>[0]) {
    return requireManager().signinRedirect(args);
  }

  function silentLogin() {
    return requireManager().signinSilent();
  }

  function logout(args?: Parameters<UserManager["signoutRedirect"]>[0]) {
    return requireManager().signoutRedirect(args);
  }

  function removeIdentity() {
    return requireManager().removeUser();
  }

  return {
    accessToken,
    profile,
    ready,
    renewFailed,
    isAuthenticated,
    roles,
    userType,
    hasAnyAdmin,
    hasAnyRole,
    canAccess,
    init,
    getIdentity,
    ensureFreshToken,
    setProfile,
    login,
    silentLogin,
    logout,
    removeIdentity,
  };
});
